import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Upload, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { useAssessmentTemplates } from '@/api/hooks/useAssessments';
import type { AssessmentTemplate } from '@/api/types/assessment.types';

// ── Customer-uploaded badge ───────────────────────────────────────────────────

function CustomerUploadedBadge({ tenantName }: { tenantName?: string }) {
  return (
    <Badge
      variant="muted"
      className="flex items-center gap-1 text-[10px] py-0"
      title="Content responsibility rests with your organisation"
    >
      <ShieldCheck className="h-2.5 w-2.5" />
      Customer-uploaded{tenantName ? ` — ${tenantName}` : ''}
    </Badge>
  );
}

// ── Template list item ────────────────────────────────────────────────────────

function TemplateRow({ template }: { template: AssessmentTemplate }) {
  const isCustomerUploaded = !template.hub_template_id && template.has_ip_attestation;

  return (
    <Link
      to={`/templates/${template.id}`}
      className="flex items-start justify-between gap-4 rounded-lg border border-border-subtle bg-bg-surface p-4 hover:bg-bg-surface-2 transition-colors"
    >
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-text-primary">{template.name}</p>
          {template.framework && (
            <Badge variant="muted" className="text-[10px] py-0">{template.framework}</Badge>
          )}
          {template.hub_template_id && (
            <Badge
              variant="muted"
              className="text-[10px] py-0"
              title="Includes VeriGRC-provided content; use remains subject to the applicable template license and platform terms."
            >
              Imported · Licensed
            </Badge>
          )}
          {isCustomerUploaded && <CustomerUploadedBadge />}
        </div>
        {template.description && (
          <p className="text-xs text-text-secondary line-clamp-1">{template.description}</p>
        )}
        <div className="flex gap-3 text-xs text-text-muted font-mono">
          <span>v{template.version} · rev {template.content_revision}</span>
          {template.total_questions > 0 && <span>{template.total_questions} questions</span>}
        </div>
      </div>
    </Link>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function TemplatesPage() {
  useEffect(() => { document.title = 'Assessment Templates — VeriGRC'; }, []);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const { data: templatesData, isLoading } = useAssessmentTemplates();
  const templates = templatesData ?? [];

  const filtered = templates.filter(
    (t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      (t.description ?? '').toLowerCase().includes(search.toLowerCase()),
  );

  function handleCreateNew() {
    navigate('/templates/new');
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary">Assessment Templates</h1>
          <p className="text-sm text-text-secondary mt-1">
            Your workspace templates. Import from{' '}
            <Link to="/templates/hub" className="text-accent-cyan hover:underline">Assessment Library</Link>{' '}
            or create your own.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/templates/hub">
              <Upload className="mr-1.5 h-4 w-4" />
              Browse Library
            </Link>
          </Button>
          <Button onClick={handleCreateNew}>
            <Plus className="mr-1.5 h-4 w-4" />
            Create Custom Template
          </Button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" />
        <Input
          placeholder="Search templates…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-border-subtle bg-bg-surface p-4 space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-full" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="space-y-4">
          <EmptyState
            icon={search ? Search : Upload}
            title={search ? 'No templates match your search' : 'No templates yet'}
            description={
              search
                ? 'Try a different search term.'
                : 'Import a Hub template or create your own to get started.'
            }
          />
          {!search && (
            <div className="flex justify-center gap-2">
              <Button variant="outline" asChild>
                <Link to="/templates/hub">Browse Library</Link>
              </Button>
              <Button onClick={handleCreateNew}>Create Custom Template</Button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((t) => (
            <TemplateRow key={t.id} template={t} />
          ))}
        </div>
      )}

    </div>
  );
}
